import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
} from "@material-ui/core";
import { AddCircleOutline, Edit as EditIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../services/api";
import { toast } from "react-toastify";
import ContactModal from "../components/ContactModal";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "absolute",
    top: 64,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#edf1f5",
    overflowY: "auto",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      top: 56,
      padding: theme.spacing(2),
      paddingTop: theme.spacing(6),
    },
  },
  titulo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(4),
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: theme.spacing(2.5, 3),
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 2),
    },
  },
  tituloTexto: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: "#111827",
  },
  botaoNovo: {
    backgroundColor: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    boxShadow: "0 3px 8px rgba(37,99,235,0.3)",
    "&:hover": {
      backgroundColor: "#1e3a8a",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: theme.spacing(0.6, 1.5),
      borderRadius: 8,
    },
  },

  botaoImportar: {
    backgroundColor: "#16a34a",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    boxShadow: "0 3px 8px rgba(37,99,235,0.3)",
    "&:hover": {
      backgroundColor: "#0e5e0eff",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: theme.spacing(0.6, 1.5),
      borderRadius: 8,
    },
  },

  listContainer: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: theme.spacing(2),
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },

  // list item responsive tweaks
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  avatar: {
    width: 56,
    height: 56,
    [theme.breakpoints.down("sm")]: {
      width: 44,
      height: 44,
    },
  },
  itemActions: {
    marginLeft: "auto",
    display: "flex",
    gap: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      alignSelf: "flex-end",
    },
  },
  headerActions: {
    display: "flex",
    gap: 10,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      width: "100%",
      "& > *": { width: "100%" },
    },
  },
  listPrimary: {
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.95rem",
    },
  },
}));

export default function ContactsPage() {
  const classes = useStyles();
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalAberto, setModalAberto] = useState(false);
  const [contatoSelecionado, setContatoSelecionado] = useState(null);

  const abrirModalNovo = () => {
    setContatoSelecionado(null);
    setModalAberto(true);
  };

  const abrirModalEditar = (contato) => {
    setContatoSelecionado(contato);
    setModalAberto(true);
  };

  // 🔍 Verificar número WhatsApp
  const verificarWhatsApp = async (telefone) => {
    try {
      const cleaned = telefone.replace(/\D/g, "");

      const { data } = await api.post("/user/check", {
        Phone: [cleaned],
      });

      const user = data?.data?.Users?.[0];

      if (!user) return false;

      return user.IsInWhatsapp === true;
    } catch (err) {
      console.error("Erro ao verificar WhatsApp:", err);
      return false;
    }
  };

  // 📌 Carregar contatos
  const carregarContatos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts"); // ✔ SEMPRE ASSIM
      setContatos(data.data);
    } catch (err) {
      toast.error("Erro ao carregar contatos");
    } finally {
      setLoading(false);
    }
  };

  // 📌 Criar ou atualizar contato
  const salvarContato = async (dados) => {
    try {
      const isValid = await verificarWhatsApp(dados.telefone);

      if (!isValid) {
        toast.error("Este número não possui WhatsApp!");
        return;
      }

      if (dados.id) {
        await api.put(`/contacts/${dados.id}`, dados);
        toast.success("Contato atualizado!");
      } else {
        await api.post(`/contacts`, dados);
        toast.success("Contato criado!");
      }

      carregarContatos();
    } catch (err) {
      toast.error("Erro ao salvar contato");
    }
  };

  // 🗑 Excluir contato
  const excluirContato = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este contato?")) return;

    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contato excluído!");
      carregarContatos();
    } catch (err) {
      toast.error("Erro ao excluir contato");
    }
  };

  useEffect(() => {
    carregarContatos();
  }, []);

  const [importando, setImportando] = useState(false);

  const importarContatos = async () => {
    setImportando(true);

    try {
      toast.info("📥 Importando contatos do WhatsApp...");

      const { data } = await api.get("/contacts/importar");

      if (!data.success) {
        toast.error(data.error || "Erro ao importar contatos.");
        return;
      }

      const totalApi = data.totalApi || 0;
      const imported = data.imported || 0;

      if (totalApi === 0) {
        toast.warning("Nenhum contato retornado da API.");
      } else {
        toast.success(`✔️ ${imported} de ${totalApi} contatos importados!`);
      }

      await carregarContatos();

    } catch (err) {
      console.error("Erro ao importar contatos:", err);
      toast.error("Erro ao importar contatos.");
    } finally {
      setImportando(false);
    }
  };


  return (
    <Box className={classes.container}>
      <Paper elevation={0} className={classes.titulo}>
        <Typography className={classes.tituloTexto}>📇 Contatos</Typography>

        {/* Botões: Novo + Importar */}
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant="contained"
            className={classes.botaoNovo}
            startIcon={<AddCircleOutline />}
            onClick={abrirModalNovo}
          >
            Novo Contato
          </Button>

          <Button
            variant="contained"
            className={classes.botaoImportar}
            color="default"
            onClick={importarContatos}
            disabled={importando}
          >
            {importando ? "Importando..." : "Importar Contatos"}
          </Button>
        </div>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper className={classes.listContainer}>
          <List>
            {contatos.map((c) => (
              <ListItem key={c.id} divider className={classes.listItem}>
                <ListItemAvatar>
                  <Avatar src={c.photoUrl || ""} style={{ backgroundColor: "#2563eb" }} className={classes.avatar}>
                    {!c.photoUrl && c.nome?.[0]?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={c.nome || "Sem nome"}
                  secondary={c.telefone}
                  classes={{ primary: classes.listPrimary }}
                />

                {/* Editar */}
                <div className={classes.itemActions}>
                  <IconButton onClick={() => abrirModalEditar(c)} title="Editar">
                    <EditIcon />
                  </IconButton>

                  {/* Deletar */}
                  <IconButton onClick={() => excluirContato(c.id)} title="Excluir">
                    <DeleteIcon color="error" />
                  </IconButton>
                </div>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <ContactModal
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        onSalvar={salvarContato}
        contato={contatoSelecionado}
      />
    </Box>
  )
};

